import type { AxiosResponse } from 'axios'

import type {
  AccountResponseDto,
  CheckExistingResponse as CheckExistingResponse,
  CreatePersonDto,
  IdentityProviderType,
  VerifyEmailCodeResponse,
  Person,
} from '@b-stock/account-api-client'

import appConfig from '@config/config'
import { sanitizeEmail } from '@helpers/sanitize'
import { get, post } from '@helpers/xhr'

const { api } = appConfig

type EntBuyerRegistration = {
  siteAbb: string
  status: string
}

// `CheckExistingResponse` marks all but `identityProviderType` optional, and
// the BE can omit even that one (see below), so we strengthen the type promise
// by ensuring some of the booleans can't be undefined (we convert an undefined
// value to false). `identityProviderType` stays optional here — a caller must
// be able to tell "omitted" apart from a real provider; defaulting it would
// silently misroute a Salesforce person as FusionAuth.
/**
 * Status of the invitation an unactivated person holds. PENDING and EXPIRED
 * are open invitations (`isInvited` is also true); CANCELLED means the account
 * owner withdrew it. Omitted when there is no such invitation, and by account
 * services that predate the field.
 */
export type InvitationStatus = 'PENDING' | 'EXPIRED' | 'CANCELLED'

// Fields the BE sends that the generated `@b-stock/account-api-client` types
// don't have yet; added by hand until the client package is regenerated.
type CheckExistingInvitationFields = {
  // true only for an open — pending or expired — invitation
  isInvited?: boolean
  invitationStatus?: InvitationStatus
}

type InternalCheckExistingResponse = Omit<
  CheckExistingResponse,
  'identityProviderType'
> & {
  hasCredentials: boolean
  isEmailVerified: boolean
  exists: boolean
  identityProviderType?: IdentityProviderType
} & CheckExistingInvitationFields

export default {
  async checkUserExists({
    email,
  }: {
    email: string
  }): Promise<InternalCheckExistingResponse> {
    // `Partial<>` because the BE returns a (mostly) empty object when the user
    // doesn't exist — including omitting `identityProviderType`, which the
    // generated type marks required.
    const { data } = await get<
      Partial<CheckExistingResponse> & CheckExistingInvitationFields
    >(api.people.checkExisting, { email: sanitizeEmail(email) })

    // Set defaults for optional fields. BE returns a (mostly) empty object
    // if the user does't exist.
    return {
      hasCredentials: false,
      isEmailVerified: false,
      exists: !!data._id,
      ...data,
    }
  },

  async checkEnterpriseAccountExists({ email }: { email: string }) {
    const response = await get(api.bridge.uaExists, {
      email: sanitizeEmail(email),
    })
    return response
  },

  async checkMageExists({ email }: { email: string }) {
    const { data } = await get<EntBuyerRegistration[]>(api.bridge.mageExists, {
      email: sanitizeEmail(email),
    })
    return data
  },

  // Returns the whole response — `store/auth/login/saga.ts` reads `.status`.
  async migrateMagentoUser() {
    const response = await post(api.bridge.accountsMigrate, null)
    return response
  },

  async checkBridExists(queryParams: { brid: string; country: string }) {
    if (!queryParams.brid) {
      return false
    }

    const { data } = await get<AccountResponseDto>(
      api.accounts.default,
      queryParams
    )
    return !!data.accounts.length
  },

  submitEmail(
    data: Omit<CreatePersonDto, 'termsAccepted'>
  ): Promise<AxiosResponse<Person>> {
    const sanitizedData: CreatePersonDto = {
      ...data,
      email: sanitizeEmail(data.email),
      termsAccepted: true,
    }
    return post(api.people.default, sanitizedData)
  },

  fetchVerificationCode({ id }: { id: string }): Promise<AxiosResponse<void>> {
    return post(api.people.id.resendValidationEmail, {}, { entities: { id } })
  },

  async verifyCode({
    peopleId,
    verificationCode,
  }: {
    peopleId: string
    verificationCode: string
  }) {
    const response = await get<VerifyEmailCodeResponse>(
      api.people.id.verifyEmailCode,
      { verificationCode },
      { entities: { id: peopleId } }
    )
    return response
  },
}
